import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Plus } from "lucide-react";
import { Button, FormModal, TableFlat } from "@veorest/ui";
import {
  createAdminPayment,
  fetchAdminClients,
  fetchAdminPayments,
  fetchAdminPlans,
  updateAdminPaymentStatus,
} from "@veorest/api-client";
import { PageHeader } from "../components/shell/PageHeader";
import { AdminPager, AdminPanel } from "../components/shell/AdminPanel";
import { useAdminFeatureFlags } from "../lib/useAdminFeatureFlags";
import { EmptyState } from "../components/data/EmptyState";
import { formatDate, formatIqd, statusPillClass } from "../lib/format";

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "paid", label: "Paid" },
  { key: "failed", label: "Failed" },
] as const;

export default function PaymentsPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [packageId, setPackageId] = useState("");
  const [amountIqd, setAmountIqd] = useState("");
  const [credits, setCredits] = useState("");
  const [payStatus, setPayStatus] = useState<"pending" | "paid">("paid");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");

  const flags = useAdminFeatureFlags();

  const payments = useQuery({
    queryKey: ["admin-payments", status, page],
    queryFn: () => fetchAdminPayments({ status: status || undefined, page, limit: 25 }),
  });
  const clients = useQuery({
    queryKey: ["admin-clients-options"],
    queryFn: () => fetchAdminClients({ limit: 100 }),
  });
  const plans = useQuery({
    queryKey: ["admin-plans"],
    queryFn: fetchAdminPlans,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createAdminPayment({
        tenantId,
        packageId: packageId || undefined,
        amountIqd: amountIqd ? Number(amountIqd) : undefined,
        credits: credits ? Number(credits) : undefined,
        status: payStatus,
        method: "manual",
        reference: reference || undefined,
        note: note || undefined,
      }),
    onSuccess: () => {
      toast.success("Payment recorded");
      setOpen(false);
      setTenantId("");
      setPackageId("");
      setAmountIqd("");
      setCredits("");
      setReference("");
      setNote("");
      void qc.invalidateQueries({ queryKey: ["admin-payments"] });
      void qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) => updateAdminPaymentStatus(id, next),
    onSuccess: () => {
      toast.success("Payment updated");
      void qc.invalidateQueries({ queryKey: ["admin-payments"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const columns = useMemo(
    () => [
      {
        key: "restaurant_name",
        header: "Client",
        render: (row: Record<string, unknown>) => (
          <span className="font-bold">{String(row.restaurant_name)}</span>
        ),
      },
      {
        key: "amount_iqd",
        header: "Amount",
        render: (row: Record<string, unknown>) => (
          <span className="admin-money">{formatIqd(Number(row.amount_iqd))}</span>
        ),
      },
      {
        key: "credits",
        header: "Credits",
        render: (row: Record<string, unknown>) => formatIqd(Number(row.credits)),
      },
      {
        key: "status",
        header: "Status",
        render: (row: Record<string, unknown>) => (
          <span className={statusPillClass(String(row.status))}>{String(row.status)}</span>
        ),
      },
      {
        key: "method",
        header: "Method",
        render: (row: Record<string, unknown>) => String(row.method),
      },
      {
        key: "created_at",
        header: "Created",
        render: (row: Record<string, unknown>) => (
          <span className="text-[var(--text-muted)]">{formatDate(String(row.created_at))}</span>
        ),
      },
      {
        key: "actions",
        header: "",
        render: (row: Record<string, unknown>) =>
          row.status === "pending" && flags.manualPayments ? (
            <button
              className="btn-ghost text-sm"
              onClick={(e) => {
                e.stopPropagation();
                statusMutation.mutate({ id: String(row.id), next: "paid" });
              }}
            >
              Mark paid
            </button>
          ) : null,
      },
    ],
    [statusMutation, flags.manualPayments]
  );

  const totalPages = Math.max(1, Math.ceil((payments.data?.total ?? 0) / 25));
  const items = (payments.data?.items ?? []) as Array<Record<string, unknown> & { id: string }>;

  return (
    <div className="menu-page">
      <PageHeader
        eyebrow="Billing"
        title="Payments"
        subtitle="Manual credit purchases today — gateway-ready tomorrow."
        actions={
          flags.manualPayments ? (
            <Button
              className="min-h-[40px]"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setOpen(true)}
            >
              Record payment
            </Button>
          ) : undefined
        }
      />

      <AdminPanel
        toolbar={
          <div className="customer-filter-chips admin-filter-chips">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key || "all"}
                type="button"
                className={`customer-filter-chip ${status === f.key ? "customer-filter-chip--active" : ""}`}
                onClick={() => {
                  setPage(1);
                  setStatus(f.key);
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        }
      >
        {items.length === 0 && !payments.isLoading ? (
          <EmptyState
            icon={<Plus className="h-5 w-5" />}
            title="No payments yet"
            body="Record a manual credit purchase to get started."
            actionLabel="Record payment"
            onAction={() => setOpen(true)}
          />
        ) : (
          <div className="admin-table-wrap">
            <TableFlat columns={columns} data={items} keyField="id" />
          </div>
        )}
      </AdminPanel>

      <AdminPager
        page={page}
        totalPages={totalPages}
        total={payments.data?.total ?? 0}
        label="payments"
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />

      <FormModal
        open={open}
        onClose={() => setOpen(false)}
        title="Record payment"
        subtitle="Link a credit purchase to a client."
        eyebrow="Payments"
        icon={<Plus className="w-5 h-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-ghost" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={!tenantId || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? "Saving…" : "Save payment"}
            </button>
          </div>
        }
      >
        <div className="space-y-3 p-1">
          <label className="block space-y-1">
            <span className="text-sm font-medium">Client</span>
            <select
              className="input-base"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              required
            >
              <option value="">Select…</option>
              {(clients.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Package (optional)</span>
            <select
              className="input-base"
              value={packageId}
              onChange={(e) => {
                const id = e.target.value;
                setPackageId(id);
                const pkg = plans.data?.packages.find((p) => String(p.id) === id);
                if (pkg) {
                  setCredits(String(pkg.credits));
                  setAmountIqd(String(pkg.price_iqd));
                }
              }}
            >
              <option value="">Custom amount</option>
              {(plans.data?.packages ?? []).map((p) => (
                <option key={String(p.id)} value={String(p.id)}>
                  {String(p.name)} — {formatIqd(Number(p.credits))}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1">
              <span className="text-sm font-medium">Amount IQD</span>
              <input
                className="input-base"
                type="number"
                value={amountIqd}
                onChange={(e) => setAmountIqd(e.target.value)}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Credits</span>
              <input
                className="input-base"
                type="number"
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
              />
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Status</span>
            <select
              className="input-base"
              value={payStatus}
              onChange={(e) => setPayStatus(e.target.value as "pending" | "paid")}
            >
              <option value="paid">Paid (apply credits now)</option>
              <option value="pending">Pending</option>
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Reference</span>
            <input
              className="input-base"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Note</span>
            <input className="input-base" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
        </div>
      </FormModal>
    </div>
  );
}
